import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const dayLabel = (iso) => { const d = new Date(`${iso}T12:00:00`); return { dow: d.toLocaleDateString('en-US', { weekday: 'short' }), day: d.getDate(), mon: d.toLocaleDateString('en-US', { month: 'short' }) }; };
const localTime = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } };

// Interactive date + time picker fed by GET /api/platform/demo-availability. Times are shown in the visitor's local timezone.
export const DemoSlotPicker = ({ value, onChange, onStatus = () => {} }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [date, setDate] = useState('');
  const [page, setPage] = useState(0);
  const PER = 7;

  useEffect(() => {
    axios.get('/platform/demo-availability').then((r) => { setData(r.data); setDate(r.data.days[0]?.date || ''); onStatus(r.data.days?.length ? 'ready' : 'empty'); })
      .catch(() => { setError('Could not load available times. You can still submit and we will email you to schedule.'); onStatus('error'); });
  }, []);

  const days = data?.days || [];
  const visible = days.slice(page * PER, page * PER + PER);
  const selectedDay = useMemo(() => days.find((d) => d.date === date), [days, date]);
  const tz = browserTz();
  const sameTz = tz === data?.timezone;

  if (error) return <p data-testid="slot-picker-error" className="rounded-xl border border-amber-500/30 bg-amber-950/30 px-4 py-3 text-xs text-amber-200">{error}</p>;
  if (!data) return <div data-testid="slot-picker-loading" className="h-28 animate-pulse rounded-xl bg-slate-900/60" />;
  if (!days.length) return <p data-testid="slot-picker-empty" className="text-xs text-slate-400">No open demo times right now — submit the form and we'll reach out to schedule.</p>;

  return (
    <div data-testid="demo-slot-picker" className="rounded-xl border border-slate-800 bg-slate-950/60 p-3 sm:p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-200">1. Pick a date</span>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Earlier dates" data-testid="slot-dates-prev" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-800 text-slate-300 disabled:opacity-30"><ChevronLeft size={16} /></button>
          <button type="button" aria-label="Later dates" data-testid="slot-dates-next" disabled={(page + 1) * PER >= days.length} onClick={() => setPage((p) => p + 1)} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-800 text-slate-300 disabled:opacity-30"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
        {visible.map((d) => { const l = dayLabel(d.date); const active = d.date === date; return (
          <button key={d.date} type="button" data-testid={`slot-date-${d.date}`} onClick={() => { setDate(d.date); onChange(null); }}
            className={`rounded-xl border px-1 py-2 text-center transition-colors ${active ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-slate-800 bg-slate-900/70 text-slate-300 hover:border-slate-600'}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-80">{l.dow}</div>
            <div className="text-lg font-bold leading-tight">{l.day}</div>
            <div className="text-[10px] opacity-80">{l.mon}</div>
          </button>
        ); })}
      </div>
      {selectedDay && (
        <>
          <div className="mb-2 mt-4 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-slate-200">2. Pick a time</span>
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"><Clock size={11} /> {sameTz || !tz ? `Times in ${data.timezone.replace('_', ' ')}` : `Shown in your time zone (${tz.replace('_', ' ')})`}</span>
          </div>
          <div data-testid="slot-times" className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {selectedDay.slots.map((s) => { const active = value?.start === s.start; return (
              <button key={s.start} type="button" data-testid={`slot-time-${s.start}`} onClick={() => onChange({ ...s, date: selectedDay.date })}
                className={`rounded-lg border px-2 py-2 text-sm font-medium transition-colors ${active ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-slate-800 bg-slate-900/70 text-slate-200 hover:border-slate-600'}`}>
                {sameTz || !tz ? s.label : localTime(s.start)}
              </button>
            ); })}
          </div>
        </>
      )}
      {value && <p data-testid="slot-selected" className="mt-3 text-xs text-emerald-300">Selected: {new Date(value.start).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ({data.slot_minutes} min)</p>}
    </div>
  );
};
